import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

export default function SetupPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("This setup link is invalid or has expired. Ask your admin to resend it.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/setup-password", { token, new_password: password });
      // V1.1: dealers land on the resale-certificate step right after activation (skippable)
      const me = await api.get("/auth/me");
      window.location.href = me.data?.role === "admin" ? "/" : "/dealer/certificate?welcome=1"; // full reload: fresh cookies → AuthContext /me → role home
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6" data-testid="setup-password-page">
      <div className="w-full max-w-sm page-enter">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
          Zelligio Connect
        </p>
        <h1 className="mt-4 font-display text-4xl font-light tracking-tight text-charcoal">
          Set your password
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-warmgrey">
          Choose a password to activate your account. This one-time setup link expires and
          cannot be reused.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="setup-password-form">
          {error && (
            <Alert variant="destructive" data-testid="setup-password-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">New password</Label>
            <PasswordInput
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="setup-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 border-border bg-white focus-visible:ring-terracotta"
            />
            <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Confirm password</Label>
            <PasswordInput
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="setup-password-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-11 border-border bg-white focus-visible:ring-terracotta"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="setup-password-submit"
            className="h-11 w-full bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password & continue"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Link expired?{" "}
            <Link to="/login" className="text-terracotta hover:underline" data-testid="setup-password-login-link">
              Sign in
            </Link>{" "}
            or ask your admin to resend the invitation.
          </p>
        </form>
      </div>
    </div>
  );
}
